import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Bookmark, LogOut, Plus, ChevronDown, Sun, Moon } from 'lucide-react';
import { useAuthStore } from '../../store/authStore.js';
import { useThemeStore } from '../../store/themeStore.js';

export default function UserMenu() {
  const { user, logout } = useAuthStore();
  const { theme, toggle } = useThemeStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="absolute top-4 right-4 z-10">
      <div className="flex gap-2">
        <button onClick={() => navigate('/contribute')} className="btn btn-secondary flex items-center gap-2">
          <Plus size={16} />
          <span className="hidden sm:inline">Add Place</span>
        </button>
        <button
          onClick={toggle}
          className="btn btn-secondary flex items-center justify-center !px-2"
          aria-label="Toggle theme"
        >
          {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
        </button>
        {user ? (
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors dark:border dark:border-gray-700"
          >
            <div className="w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center text-brand-700 dark:text-brand-300 text-sm font-medium">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <ChevronDown size={16} className="text-gray-400 dark:text-gray-500" />
          </button>
        ) : (
          <button onClick={() => navigate('/login')} className="btn btn-primary">
            Sign In
          </button>
        )}
      </div>

      {open && user && (
        <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden min-w-48 dark:border dark:border-gray-700">
          <div className="p-3 border-b border-gray-100 dark:border-gray-800">
            <div className="font-medium text-gray-800 dark:text-gray-200 text-sm">{user.name}</div>
            <div className="text-xs text-gray-400 dark:text-gray-500">{user.email}</div>
          </div>
          <button
            onClick={() => {
              navigate('/profile');
              setOpen(false);
            }}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
          >
            <User size={16} />
            Profile
          </button>
          <button
            onClick={() => {
              navigate('/profile');
              setOpen(false);
            }}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
          >
            <Bookmark size={16} />
            Saved Places
          </button>
          {user.role === 'admin' && (
            <button
              onClick={() => {
                navigate('/admin');
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
            >
              Admin Dashboard
            </button>
          )}
          <button
            onClick={() => {
              logout();
              navigate('/');
              setOpen(false);
            }}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
